import React, { useState, useEffect, useRef } from 'react';

function ActivityFeed({ activities, isOpen, onToggle }) {
  const [filter, setFilter] = useState('all');
  const [newActivityCount, setNewActivityCount] = useState(0);
  const [lastViewedTime, setLastViewedTime] = useState(Date.now());
  const activitiesRef = useRef(null);
  const previousActivitiesLength = useRef(activities.length);

  // Track new activities when feed is closed
  useEffect(() => {
    if (!isOpen && activities.length > previousActivitiesLength.current) {
      const newCount = activities.length - previousActivitiesLength.current;
      setNewActivityCount(prev => prev + newCount);
    }
    previousActivitiesLength.current = activities.length;
  }, [activities.length, isOpen]);

  // Reset new activity count when feed is opened
  useEffect(() => {
    if (isOpen) {
      setNewActivityCount(0);
      setLastViewedTime(Date.now());
    }
  }, [isOpen]);

  // Auto-scroll to top when new activities arrive
  useEffect(() => {
    if (isOpen && activitiesRef.current) {
      activitiesRef.current.scrollTop = 0;
    }
  }, [activities, isOpen]);

  const filteredActivities = activities.filter(activity => {
    if (filter === 'all') return true;
    return activity.action_type === filter;
  });

  const getActivityIcon = (actionType) => {
    const icons = {
      'moved_ticket': '🔄',
      'updated_ticket': '✏️',
      'created_ticket': '➕',
      'deleted_ticket': '🗑️',
      'status_changed': '📋',
      'priority_changed': '⚡',
      'assigned_ticket': '👤',
      'commented': '💬',
      'created_board': '📋',
      'updated_board': '📝',
      'deleted_board': '❌',
      'reordered_boards': '↕️',
      'reordered_tickets': '🔃',
      'updated_board_status': '🔄',
      'imported_tickets': '📥'
    };
    return icons[actionType] || '📝';
  };

  const getActivityMessage = (activity) => {
    const { action_type, ticket_key, from_board, to_board, details } = activity;
    
    switch (action_type) {
      case 'moved_ticket':
        return `moved ${ticket_key} from ${from_board} to ${to_board}`;
      case 'updated_ticket':
        return `updated ${ticket_key}`;
      case 'created_ticket':
        return `created ticket ${ticket_key}`;
      case 'deleted_ticket':
        return `deleted ticket ${ticket_key}`;
      case 'status_changed':
        return `changed ${ticket_key} status to ${details?.newStatus}`;
      case 'priority_changed':
        return `changed ${ticket_key} priority to ${details?.newPriority}`;
      case 'created_board':
        return `created board "${details?.boardName}"`;
      case 'updated_board':
        return `updated board "${details?.boardName}"`;
      case 'deleted_board':
        return `deleted board "${details?.boardName}"`;
      case 'reordered_boards':
        return `reordered boards`;
      case 'reordered_tickets':
        return `reordered tickets in ${details?.boardName}`;
      case 'updated_board_status':
        return `changed "${details?.boardName}" status to ${details?.newStatus}`;
      case 'imported_tickets':
        return `imported ${details?.count} tickets from ${details?.fileName}`;
      default:
        return `performed ${action_type} on ${ticket_key || 'a ticket'}`;
    }
  };

  const getUserColor = (email) => {
    if (!email) return '#007bff';
    const colors = ['#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8', '#6f42c1', '#fd7e14'];
    const hash = email.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  const isRecentActivity = (activity) => {
    const activityTime = new Date(activity.created_at).getTime();
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    return activityTime > fiveMinutesAgo;
  };

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          cursor: 'pointer',
          fontSize: '24px',
          zIndex: 1000,
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
        }}
        title="Activity Feed"
      >
        📢
        {newActivityCount > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: '#dc3545',
              color: 'white',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              border: '2px solid white',
              animation: 'pulse 2s infinite'
            }}
          >
            {newActivityCount > 99 ? '99+' : newActivityCount}
          </div>
        )}
      </button>
    );
  }

  return (
    <>
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: '400px',
        maxHeight: '600px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        border: '1px solid #e0e0e0',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
            📢 Live Activity
            {activities.length > 0 && (
              <span style={{
                marginLeft: '8px',
                fontSize: '12px',
                fontWeight: 'normal',
                color: '#666'
              }}>
                ({activities.length})
              </span>
            )}
          </h3>
          <button
            onClick={onToggle}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            ✖
          </button>
        </div>

        {/* Filters */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          {['all', 'moved_ticket', 'created_ticket', 'updated_ticket', 'created_board', 'updated_board'].map(filterType => (
            <button
              key={filterType}
              onClick={() => setFilter(filterType)}
              style={{
                padding: '4px 12px',
                fontSize: '12px',
                border: '1px solid #ddd',
                borderRadius: '16px',
                backgroundColor: filter === filterType ? '#007bff' : 'white',
                color: filter === filterType ? 'white' : '#666',
                cursor: 'pointer',
                textTransform: 'capitalize',
                transition: 'all 0.2s ease'
              }}
            >
              {filterType.replace('_', ' ')}
            </button>
          ))}
        </div>

        {/* Activity List */}
        <div 
          ref={activitiesRef}
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '8px'
          }}
        >
          {filteredActivities.length === 0 ? (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              {filter === 'all' ? 'No recent activity' : `No ${filter.replace('_', ' ')} activities`}
            </div>
          ) : (
            filteredActivities.map(activity => (
              <div
                key={activity.id}
                style={{
                  padding: '12px',
                  margin: '4px 0',
                  borderRadius: '8px',
                  backgroundColor: isRecentActivity(activity) ? '#f0f8ff' : '#f8f9fa',
                  border: isRecentActivity(activity) ? '1px solid #007bff' : '1px solid #e9ecef',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = isRecentActivity(activity) ? '#e3f2fd' : '#e9ecef';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isRecentActivity(activity) ? '#f0f8ff' : '#f8f9fa';
                }}
              >
                {isRecentActivity(activity) && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#28a745',
                      borderRadius: '50%',
                      animation: 'pulse 2s infinite'
                    }}
                  />
                )}
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '4px'
                }}>
                  <span style={{ fontSize: '16px' }}>
                    {getActivityIcon(activity.action_type)}
                  </span>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: getUserColor(activity.user_email),
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    {(activity.user_name || activity.user_email || 'U')[0].toUpperCase()}
                  </div>
                  <span style={{ fontWeight: '500', color: '#007bff', flex: 1 }}>
                    {activity.user_name || activity.user_email}
                  </span>
                  <span style={{ fontSize: '12px', color: '#999' }}>
                    {getTimeAgo(activity.created_at)}
                  </span>
                </div>
                <div style={{ color: '#555', marginLeft: '32px' }}>
                  {getActivityMessage(activity)}
                </div>
                
                {/* Additional details for some activities */}
                {activity.details && (
                  <div style={{ 
                    marginLeft: '32px', 
                    marginTop: '4px', 
                    fontSize: '12px', 
                    color: '#888',
                    fontStyle: 'italic'
                  }}>
                    {activity.action_type === 'imported_tickets' && (
                      `Created: ${activity.details.created}, Updated: ${activity.details.updated}`
                    )}
                    {activity.action_type === 'moved_ticket' && activity.details.summary && (
                      `"${activity.details.summary}"`
                    )}
                    {activity.action_type === 'updated_board_status' && (
                      `From: ${activity.details.oldStatus} → To: ${activity.details.newStatus}`
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer with connection status */}
        <div style={{
          padding: '8px 16px',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          fontSize: '12px',
          color: '#666',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>Real-time updates</span>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <div
              style={{
                width: '8px',
                height: '8px',
                backgroundColor: '#28a745',
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }}
            />
            <span>Live</span>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </>
  );
}

// Helper function (same as in UserPresence)
function getTimeAgo(timestamp) {
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default ActivityFeed;